import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Est = { id: string; status?: string; approved_at?: string | null; signature_data?: string | null; signed_pdf_path?: string | null; project_id?: string | null; sent_at?: string | null; voided_at?: string | null; archived_at?: string | null };

export function canHardDeleteEstimate(e: Pick<Est, "status" | "approved_at" | "signature_data" | "signed_pdf_path" | "project_id" | "sent_at" | "voided_at">): boolean {
  return (
    e.status === "Draft" &&
    !e.approved_at &&
    !e.signature_data &&
    !e.signed_pdf_path &&
    !e.project_id &&
    !e.sent_at &&
    !e.voided_at
  );
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["estimates"] });
  qc.invalidateQueries({ queryKey: ["estimate_detail"] });
}

export function useDeleteEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("delete_estimate" as any, { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: () => { toast.success("Estimate deleted"); invalidate(qc); },
    onError: (err: any) => toast.error(err?.message?.replace(/^[A-Z_:]+\s*/, "") || "Could not delete estimate"),
  });
}

export function useVoidEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data, error } = await supabase.rpc("void_estimate" as any, { p_id: id, p_reason: reason });
      if (error) throw error;
      return data;
    },
    onSuccess: () => { toast.success("Estimate voided"); invalidate(qc); },
    onError: (err: any) => toast.error(err?.message?.replace(/^[A-Z_:]+\s*/, "") || "Could not void estimate"),
  });
}

export function useArchiveEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archive }: { id: string; archive: boolean }) => {
      const { data, error } = await supabase.rpc("archive_estimate" as any, { p_id: id, p_archive: archive });
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => { toast.success(vars.archive ? "Estimate archived" : "Estimate restored"); invalidate(qc); },
    onError: (err: any) => toast.error(err?.message?.replace(/^[A-Z_:]+\s*/, "") || "Action failed"),
  });
}
